import { makeStyles, RACButton, RACModal } from '@rentacenter/racstrap';
import clsx from 'clsx';
import { chain, isArray } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useAsync } from 'react-async';
import { FormProvider, get, useForm } from 'react-hook-form';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { getPurchaseOrderById, updateFunding } from '../../api/purchaseOrders';
import { AppRoute } from '../../config/route-config';
import {
  PoFormField,
  TIRES_CLASSIFICATIONS_SELECT,
  TIRES_DEPARTMENT,
  TIRES_MANUALLY_RECEIVED_BRACKET,
  TIRES_SUB_DEPARTMENT,
  TIRES_WHEEL_ITEM_SUB_TYPE,
  TIRES_WHEEL_ITEM_TYPE,
  OTHER_DEPARTMENT,
  OTHER_SUB_DEPARTMENT,
} from '../../constants/constants';
import { useGlobalContext } from '../../context/global/GlobalContext';
import {
  checkLegalLimits,
  createDraftPurchaseOrder,
  createSubmitPurchaseOrder,
  handledCreateDraftPurchaseOrder,
  SUBMIT_ERROR_MESSAGE,
} from '../../context/single/purchaseOrderThunks';
import { LegalEngineErrorCode } from '../../domain/Legal/LegalEngineError';
import { toIllegalResponseList } from '../../domain/Legal/LegalEngineResponseItem';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { PurchaseOrder } from '../../domain/PurchaseOrder/PurchaseOrder';
import { getStoreByNumber, Store } from '../../domain/Store';
import {
  LegalEngineResponseItem,
  PurchaseOrderFormValues,
  APIError,
  LegalEngineRequest,
  StoreConfigsResponse,
  QrScannerOutput,
} from '../../types/types';
import { Either, left } from '../../utils/Either';
import { generateFormDefaults } from '../../utils/generateFormDefaults';
import { PricingCalculator } from '../../utils/PricingCalculator';
import {
  toLegalEngineRequest,
  toLocalSourceLegalEngineRequests,
} from '../../utils/purchaseOrderFormMappers';
import {
  IllegalStateInfo,
  IllegalValueBox,
} from './illegal-values-box/IllegalValuesBox';
import { calculateValue, noOp } from '../../utils/utils';
import { hasMaxQuantityError } from '../Allocations/TotalQuantity';
import { Allocations } from './Allocations';
import { CreatePOFooter } from './CreatePOFooter';
import { DetailsTitle } from './DetailsTitle';
import { InventoryDetails } from './InventoryDetails';
import { ItemDetails } from './ItemDetails';
import { PODetailsBreadcrumb } from './PODetailsBreadcrumb';
import { PricingWrapper } from './PricingWrapper';
import { AlignmentBox } from './alignment-box/AlignmentBox';
import { AlertModal } from '../POs/AlertModal';
import { LoadingOverlay } from '../../common/LoadingOverlay/LoadingOverlay';
import { CancelPOConfirmModal } from '../POs/CancelPOConfirmModal';
import { CANCELLED_SUCCESS } from '../../constants/constants';
import { useDuplicatePo } from './useDuplicatePo';
import { LooseWorkModal } from '../POs/LooseWorkModal';
import { getmenuandstore, getStoreConfigs } from '../../api/store';
import { CameraDetectConfirmModal } from '../POs/CameraDetectConfirmModal';
import { CameraCaptureModal } from '../POs/CameraCaptureModal';
import { checkFlag, checkFlagWithDefault } from '../../common/ConfigFlags';
import SecondFactor from '../SecondLevelAuthentication/TwoFactorPopup';
import { LocalSourceRampDetails } from './LocalSourceRampDetails';
import { PrefillClassifications } from './usePreFillFromDashboard';

const CATEGORY_FIELDS = [
  'department',
  'subDepartment',
  'bracket',
  'itemType',
  'itemSubType',
];

function resolveRmsItemNumber(
  formValues: PurchaseOrderFormValues,
  isLocalSource: boolean,
  scannerOutput: QrScannerOutput | undefined
): string {
  if (isLocalSource) {
    return (
      formValues.rmsItemNumber ||
      formValues.items?.find((item: any) => item?.rmsItemNumber)
        ?.rmsItemNumber ||
      (scannerOutput?.isScannedCategoryKept
        ? scannerOutput?.rmsItemNumber?.toString()
        : '') ||
      ''
    );
  }
  return formValues.rmsItemNumber === '0' ||
    formValues.rmsItemNumber === '' ||
    formValues.rmsItemNumber == null
    ? scannerOutput?.isScannedCategoryKept
      ? scannerOutput?.rmsItemNumber?.toString() ?? ''
      : ''
    : formValues.rmsItemNumber;
}

function getClassificationValue(
  formValues: PurchaseOrderFormValues,
  idx: number,
  field: string
): string {
  return (
    (idx === 0
      ? (formValues as any)[field]
      : (formValues as any).products?.[idx]?.[field]) || ''
  );
}

function getItemScanMatchId(
  formValues: PurchaseOrderFormValues,
  prefill: Record<number, PrefillClassifications>,
  idx: number
): string {
  const snap = prefill[idx];
  if (!snap) return 'M';

  // Check __unresolved flags set by classification components
  const hasUnresolved = CATEGORY_FIELDS.some((f) => {
    if (!(snap as any)[f]) return false;
    const flag = getClassificationValue(formValues, idx, f + '__unresolved');
    if (flag === 'true') return true;
    return !getClassificationValue(formValues, idx, f);
  });
  if (hasUnresolved) return 'MA';

  const isIncomplete = CATEGORY_FIELDS.some((f) => !(snap as any)[f]);
  if (isIncomplete) return 'M';

  const changed = CATEGORY_FIELDS.some(
    (f) => getClassificationValue(formValues, idx, f) !== (snap as any)[f]
  );
  return changed ? 'MP' : 'MS';
}

function determineLocalSourceScanMatchIds(
  formValues: PurchaseOrderFormValues,
  prefill: Record<number, PrefillClassifications> | null
): Record<number, string> {
  const result: Record<number, string> = {};
  if (!prefill) return result;

  const productIndexes: number[] = Array.isArray(
    formValues.localSourceProductIndexes
  )
    ? formValues.localSourceProductIndexes
    : Object.keys(prefill).map(Number);

  productIndexes.forEach((idx) => {
    result[idx] = getItemScanMatchId(formValues, prefill, idx);
  });

  return result;
}

function determineLocalSourceScanMatchId(
  formValues: PurchaseOrderFormValues,
  prefill: Record<number, PrefillClassifications> | null
): string {
  const perItem = determineLocalSourceScanMatchIds(formValues, prefill);
  const values = Object.values(perItem);
  if (!values.length) return 'M';

  const PRIORITY = ['MA', 'MP', 'M', 'MS'];
  for (const code of PRIORITY) {
    if (values.includes(code)) return code;
  }
  return 'M';
}

const DRAFT_PO_ERROR_MESSAGE =
  'We are unable to Save as Draft at this time, please try again later';
const SUBMIT_PO_ERROR_MESSAGE =
  'We are unable to Create New PO at this time, please try again later';

const EXCEED_MAX_MESSAGE =
  'The total cost max cannot equal or exceed 100000. Please adjust term/rate';

const EXCEED_MAX_MESSAGE_CASHPRICE =
  'The cash price cannot exceed total RTO price. Please adjust term/rate';

const useStyles = makeStyles((theme: any) => ({
  flex: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.typography.pxToRem(16),
  },
  bottom: {
    marginBottom: theme.typography.pxToRem(124),
  },
  dialogContent: {
    textAlign: 'center',
    margin: `2rem 0 0`,
    padding: 0,
    fontSize: theme.typography.pxToRem(16),
  },
  dialogActions: {
    margin: 'auto',
    bottom: 0,
  },
  dialogRoot: {
    height: '100%',
    '& .MuiPaper-rounded': {
      borderRadius: theme.typography.pxToRem(12),
    },
    '& .MuiDialogTitle-root': {
      display: 'none',
    },
    '& .MuiTypography-h5': {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: 500,
      lineHeight: theme.typography.pxToRem(30),
    },
  },
  footerButton: {
    height: theme.typography.pxToRem(43),
  },
  redButton: {
    //TODO: add to racstrap
    // eslint-disable-next-line sonarjs/no-duplicate-string
    backgroundColor: 'rgba(233,92,92,255)',
    '&:disabled': {
      background: 'rgba(233,92,92,255)',
      opacity: 0.5,
    },
    '&:hover': {
      backgroundColor: 'rgba(233,92,92,255)',
    },
  },
}));

export interface PODetailsFormProps
  extends RouteComponentProps<
    { purchaseOrderId: string; storeNumber?: string },
    Record<string, unknown>
  > {
  readonly purchaseOrderId: string | undefined;
  readonly purchaseOrder: PurchaseOrderFormValues | null;
  readonly stores: Store[];
  readonly isPurchaseOrderPending?: boolean;
  readonly isSaveDraftDisabled: boolean;
  readonly setIsSaveDraftDisabled?: any;
  readonly isSubmitDisabled?: boolean;
  readonly isSearchLoading?: boolean;
  readonly setIsSubmitDisabled?: any;
  readonly onSearch?: (text: string) => void;
}
// eslint-disable-next-line sonarjs/cognitive-complexity
export const PODetailsForm = (props: PODetailsFormProps) => {
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraModalMpoNumber, setCameraModalMpoNumber] = useState('');
  const [rmsItemNumber, setRmsItemNumber] = useState('');
  const [manualPurchaseOrderId, setmanualPurchaseOrderId] = useState('');
  useEffect(() => {
    if (showCameraModal) {
      // eslint-disable-next-line no-console
      console.log(
        'Opening CameraCaptureModal with MPO number (effect):',
        cameraModalMpoNumber
      );
    }
  }, [
    cameraModalMpoNumber,
    showCameraModal,
    rmsItemNumber,
    manualPurchaseOrderId,
  ]);
  const {
    purchaseOrder: purchaseorder,
    stores,
    setIsSaveDraftDisabled,
    isSearchLoading,
    onSearch,
    purchaseOrderId,
  } = props;

  const classes = useStyles();
  const [isAlignmentModalOpen, showAlignmentModal] = useState(false);
  const [isCancelDisabled, setIsCancelDisabled] = useState(false);
  const [isDraftPOReload, setIsDraftPOReload] = useState(false);
  const [isSubmittedPOReload, setIsSubmittedPOReload] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showCameraDetectModal, setShowCameraDetectModal] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [cameraDetectMessage, setCameraDetectMessage] = useState('');
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState<any>(purchaseorder);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enableMPOReceiptCapture, setEnableMPOReceiptCapture] = useState(false);
  const [localSourceRamp, setLocalSourceRamp] = useState(false);
  const [configFlagsLoaded, setConfigFlagsLoaded] = useState(false);
  const [scannerOutput, setScannerOutput] = useState<
    QrScannerOutput | undefined
  >(undefined);

  const [coupaOrPcrdSelected, setCoupaOrPcrdSelected] = useState(false);
  const [canLoseWorkPromptBeShown, setCanLoseWorkPromptBeShown] =
    useState(false);
  const [serverResponseMessage, setServerResponseMessage] = useState('');

  const [illegalResponse, setIllegalResponse] = useState<
    LegalEngineResponseItem[]
  >([]);

  const [showLooseWorkModal, setShowLooseWorkModal] = useState(false);
  const [redirectPath, setRedirectPath] = useState('');

  const globalContext = useGlobalContext();

  // SLA popup states
  const [manualPOSLAEnabled, setManualPOSLAEnabled] = useState(true);
  const [showEditSLA, setShowEditSLA] = useState(false);
  const [pendingFormValues, setPendingFormValues] =
    useState<PurchaseOrderFormValues | null>(null);
  const [currentRole, setCurrentRole] = useState('');
  const DEFAULT_ROLE = 'RD';
  let skipLegal = false;
  const [isTireItem, setIsTireItem] = useState(false);
  const methods = useForm<PurchaseOrderFormValues>({
    mode: 'onSubmit',
    defaultValues: generateFormDefaults(stores, purchaseOrder || undefined),
  });

  const {
    match: {
      params: { storeNumber },
    },
  } = props;

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    errors,
    formState: { isDirty },
    reset,
    trigger,
    setError,
    clearErrors,
  } = methods;

  const isViewMode = !!(
    purchaseOrder && ![PoStatus.DRAFT, ''].includes(purchaseOrder.poStatus)
  );

  // eslint-disable-next-line no-console
  console.log('purchaseOrder.poStatus', purchaseOrder?.poStatus?.length);
  const isEditMode = !!(
    purchaseOrder && purchaseOrder.poStatus === PoStatus.DRAFT
  );
  const isCanceled = !!(
    purchaseOrder && purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
  );

  useEffect(() => {
    async function fetchCurrentRole() {
      const currentRole = window.localStorage.getItem('currentRole')
        ? window.localStorage.getItem('currentRole')
        : await getmenuandstore('MENU', true);
      setCurrentRole(currentRole);
    }
    fetchCurrentRole();
  }, []);

  // Capture prefill classifications from location.state before usePreFillFromDashboard consumes it
  const prefillClassificationsRef = useRef<Record<
    number,
    PrefillClassifications
  > | null>(null);
  if (!prefillClassificationsRef.current) {
    const locationItems = (props.location?.state as any)?.items;
    if (Array.isArray(locationItems)) {
      const snap: Record<number, PrefillClassifications> = {};
      locationItems.slice(0, 4).forEach((item: any, idx: number) => {
        snap[idx] = {
          department: item.department || '',
          subDepartment: item.subDepartment || '',
          bracket: item.bracket || '',
          itemType: item.itemType || '',
          itemSubType: item.subType || '',
        };
      });
      prefillClassificationsRef.current = snap;
    }
  }

  const isCreateMode = purchaseOrderId === undefined;
  const isFromDashboard =
    new URLSearchParams(props.location?.search).get('dashboard') === 'true';
  const hasSpendLimitId = !!(props.location?.state as any)?.spendLimitId;
  const showLocalSourceRampLayout =
    localSourceRamp && isFromDashboard && hasSpendLimitId;
  const hasSpendLimitIdOnPO = !!purchaseOrder?.spendLimitId;
  // View mode uses spendLimitId on the PO itself.
  const useLocalSourceView =
    !isEditMode &&
    ((showLocalSourceRampLayout && !isViewMode) ||
      (hasSpendLimitIdOnPO && isViewMode));

  useEffect(() => {
    if (isEditMode) {
      setCanLoseWorkPromptBeShown(true);
      return;
    }

    if (isViewMode) {
      setCanLoseWorkPromptBeShown(false);
      return;
    }
    if (isCanceled) {
      setCanLoseWorkPromptBeShown(false);
      return;
    }
    // duplicate po
    setCanLoseWorkPromptBeShown(true);
  }, [isViewMode, isEditMode, isCanceled, isDirty]);

  useEffect(() => {
    setPurchaseOrder(purchaseorder);
  }, [purchaseorder]);

  useEffect(() => {
    const checkMPOFlags = async () => {
      const storeNumber = window.sessionStorage.getItem('storeNumber');
      try {
        if (storeNumber) {
          const [flagReceipt, flagLocalSourceRamp] = await Promise.all([
            checkFlag('EnableMPOReceiptCapture', storeNumber),
            checkFlag('EnableLocalSourcing', storeNumber),
          ]);
          setEnableMPOReceiptCapture(flagReceipt);
          setLocalSourceRamp(flagLocalSourceRamp);

          const shouldSLAconfig = await checkFlagWithDefault(
            'ManualPoSlaEnable',
            storeNumber,
            true
          );
          setManualPOSLAEnabled(shouldSLAconfig);
        }
      } finally {
        setConfigFlagsLoaded(true);
      }
    };
    checkMPOFlags();
  }, []);

  const { handleDuplicate } = useDuplicatePo(purchaseOrderId || '', {
    getValues,
    setValue,
    reset,
  });

  const shouldShowAlignmentModal = () => {
    const {
      department,
      itemSubType,
      subDepartment,
      startClassificationsSelect,
      itemType,
      bracket,
    } = getValues();

    if (isAlignmentModalOpen) return false;
    if (
      department === TIRES_DEPARTMENT &&
      subDepartment === TIRES_SUB_DEPARTMENT &&
      startClassificationsSelect === TIRES_CLASSIFICATIONS_SELECT &&
      bracket === TIRES_MANUALLY_RECEIVED_BRACKET &&
      itemType === TIRES_WHEEL_ITEM_TYPE &&
      itemSubType === TIRES_WHEEL_ITEM_SUB_TYPE
    ) {
      return true;
    }

    return false;
  };

  const SLACanceled = () => {
    setShowEditSLA(false);
  };

  // Handle SLA completion
  const handleSLACompleted = () => {
    setShowEditSLA(false);
    if (pendingFormValues) {
      onSaveSubmitted(pendingFormValues);
      setPendingFormValues(null);
    }
  };

  const hasTireAlignmentEnabled = async (
    selectedStoreNumber: string
  ): Promise<boolean> => {
    const selectedStore = selectedStoreNumber;
    const paramKeyName = 'EnableAccess';
    const storeConfig = await getStoreConfigs({
      storeNumbers: [selectedStore],
      paramKeyNames: [paramKeyName],
    });
    return checkIfTireAlignmentAllowed(storeConfig);
  };

  const checkIfTireAlignmentAllowed = (
    response: StoreConfigsResponse[]
  ): boolean => {
    let tireAlignmentAllowed = false;
    if (
      response &&
      response.length > 0 &&
      response[0].configDetails &&
      response[0].configDetails.length > 0
    ) {
      tireAlignmentAllowed = response[0].configDetails.some(
        (config) =>
          config.paramGroupName === 'MpoTireAlignment' &&
          config.paramValue == '1'
      );
    }
    return tireAlignmentAllowed;
  };

  const autofixRate = (illegalStatesInfo: IllegalStateInfo[]) => {
    const formValues: PurchaseOrderFormValues = getValues();
    const correctionsByState: any = {};
    illegalStatesInfo.forEach((illegalState) => {
      correctionsByState[illegalState.state.abbreviation] =
        illegalState.adjustedTerm;
    });
    const adjustedPricing = formValues.pricing.map((pricing) => {
      if (!correctionsByState[pricing.state]) {
        return pricing;
      }
      if (
        parseInt(pricing.weeklyTerm, 10) <= correctionsByState[pricing.state]
      ) {
        return pricing;
      }
      const weeklyTerm = String(correctionsByState[pricing.state]);
      const totalRtoPrice = calculateValue(
        PricingCalculator.totalRtoPrice,
        pricing.weeklyRent,
        weeklyTerm
      );
      const biWeeklyTerm = calculateValue(
        PricingCalculator.biWeeklyTerm,
        weeklyTerm
      );
      const monthlyRent = calculateValue(
        PricingCalculator.monthlyRent,
        pricing.weeklyRent,
        String(PricingCalculator.monthlyMultiplier())
      );
      const monthlyTerm = calculateValue(
        PricingCalculator.monthlyTerm,
        totalRtoPrice,
        monthlyRent
      );
      const semiMonthlyTerm = calculateValue(
        PricingCalculator.semiMonthlyTerm,
        monthlyTerm
      );
      return {
        ...pricing,
        weeklyTerm,
        totalRtoPrice,
        biWeeklyTerm,
        semiMonthlyTerm,
        monthlyTerm,
      };
    });
    skipLegal = true;
    onSaveSubmitted({ ...formValues, pricing: adjustedPricing });
    return false;
  };

  const redirectToDraftPurchaseOrders = (oneOrManyPurchaseOrders?: any) => {
    setIsSaveDraftDisabled(true);

    setIsSubmitting(false);
    if (isArray(oneOrManyPurchaseOrders)) {
      const submittedIds: string = oneOrManyPurchaseOrders?.reduce(
        (ids: string, po: any) => {
          if (po?.purchaseOrder) {
            return ids
              ? `${ids}, #${po.purchaseOrder.number}`
              : `${po.purchaseOrder.number}`;
          }
          return ids;
        },
        ''
      );

      if (enableMPOReceiptCapture) {
        setShowCameraDetectModal(true);
        setCameraDetectMessage(
          `Manual PO with PO #${submittedIds} submitted successfully`
        );
      } else {
        setShowAlertModal(true);
        setAlertType('success');
        setAlertMessage(
          `Manual PO with PO #${submittedIds} submitted successfully`
        );
      }
    } else {
      setIsDraftPOReload(true);
    }
  };

  const checkMaxValue = (formValues: PurchaseOrderFormValues): boolean => {
    return !!formValues?.pricing?.find(
      (item) => Number(item.totalRtoPrice) > 99999
    );
  };
  const checkCashPrice = (formValues: PurchaseOrderFormValues): boolean => {
    return !!formValues?.pricing?.find(
      (item) =>
        Number(item.totalRtoPrice) < Number(item.cashPrice ? item.cashPrice : 1)
    );
  };

  const isSelectedDeptSubDeptIsOther = (
    formValues: PurchaseOrderFormValues
  ): boolean => {
    let isSelectedDeptSubDeptIsOther = false;
    if (showEditSLA) return false;
    formValues?.department === OTHER_DEPARTMENT &&
    formValues?.subDepartment === OTHER_SUB_DEPARTMENT &&
    formValues?.startClassificationsSelect === TIRES_CLASSIFICATIONS_SELECT
      ? (isSelectedDeptSubDeptIsOther = true)
      : (isSelectedDeptSubDeptIsOther = false);
    return isSelectedDeptSubDeptIsOther;
  };

  const withScannerOutput = (
    legalPriceRequest: LegalEngineRequest
  ): LegalEngineRequest => ({
    ...legalPriceRequest,
    rmsItemNumber:
      legalPriceRequest.rmsItemNumber ||
      (scannerOutput?.isScannedCategoryKept
        ? scannerOutput?.rmsItemNumber?.toString()
        : ''),
  });

  const withLegalEngineRmsItemNumbers = (
    formValues: PurchaseOrderFormValues,
    legalEngineResponseItems: LegalEngineResponseItem[]
  ): PurchaseOrderFormValues => {
    if (!showLocalSourceRampLayout || !legalEngineResponseItems.length) {
      return formValues;
    }

    const productIndexes = Array.isArray(formValues.localSourceProductIndexes)
      ? formValues.localSourceProductIndexes
      : legalEngineResponseItems.map(
          (_: LegalEngineResponseItem, index) => index
        );
    const pricingItems = Array.isArray(formValues.items)
      ? formValues.items
      : [];
    const items = productIndexes.map((_: any, index: number) => ({
      ...(pricingItems[index] || {}),
      rmsItemNumber:
        legalEngineResponseItems[index]?.rmsItemNumber ||
        pricingItems[index]?.rmsItemNumber,
    }));
    const rmsItemNumber =
      legalEngineResponseItems.find((item: any) => item.rmsItemNumber)
        ?.rmsItemNumber ||
      formValues.rmsItemNumber ||
      items.find((item: any) => item.rmsItemNumber)?.rmsItemNumber;

    return {
      ...formValues,
      ...(rmsItemNumber && { rmsItemNumber }),
      items,
    };
  };
  // call legal Price service
  const checkLegalLimitsForFormValues = async (
    formValues: PurchaseOrderFormValues
  ): Promise<Either<LegalEngineResponseItem[], APIError>> => {
    if (skipLegal) {
      return left([]);
    }

    const legalPriceRequests = (
      showLocalSourceRampLayout
        ? toLocalSourceLegalEngineRequests(formValues)
        : [toLegalEngineRequest(formValues)]
    ).map(withScannerOutput);
    // eslint-disable-next-line no-console
    console.log('Legal engine requests:', legalPriceRequests);
    const legalResults = await Promise.all(
      legalPriceRequests.map((legalPriceRequest) =>
        checkLegalLimits(legalPriceRequest)
      )
    );
    const failedLegalResult = legalResults.find((result) => result.isRight());

    if (failedLegalResult) {
      return failedLegalResult;
    }

    return left(
      legalResults.reduce(
        (
          responseItems: LegalEngineResponseItem[],
          legalResult: Either<LegalEngineResponseItem[], APIError>
        ) =>
          legalResult.isLeft()
            ? [...responseItems, ...legalResult.value]
            : responseItems,
        []
      )
    );
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onSaveSubmitted = async (formValues: PurchaseOrderFormValues) => {
    // Restore product-catalog items from registered hidden field
    // (setValue on unregistered fields doesn't persist in RHF v6 getValues)
    if (!formValues.items && (formValues as any).productCatalogItemsJson) {
      try {
        formValues = {
          ...formValues,
          items: JSON.parse((formValues as any).productCatalogItemsJson),
        };
      } catch {
        // ignore parse errors
      }
    }

    if (checkMaxValue(formValues)) {
      setAlertMessage(EXCEED_MAX_MESSAGE);
      setAlertType('error');
      setShowAlertModal(true);
      setIsSubmitting(false);
      return;
    }
    if (checkCashPrice(formValues)) {
      setAlertMessage(EXCEED_MAX_MESSAGE_CASHPRICE);
      setAlertType('error');
      setShowAlertModal(true);
      setIsSubmitting(false);
      return;
    }
    if (isSelectedDeptSubDeptIsOther(formValues) && manualPOSLAEnabled) {
      setPendingFormValues(formValues);
      setShowEditSLA(true);
      setIsSubmitting(false);
      return;
    }

    setIllegalResponse([]);
    setIsSubmitting(true);
    const either = await checkLegalLimitsForFormValues(formValues);
    showAlignmentModal(false);
    if (either.isLeft()) {
      const responseItems: LegalEngineResponseItem[] = toIllegalResponseList(
        either.value
      );

      if (responseItems.length) {
        setIsTireItem(formValues.costOfAlignment ? true : false);
        setIllegalResponse(responseItems);
        if (formValues.costOfAlignment) {
          setIsSubmitting(true);
        } else {
          setIsSubmitting(false);
          return;
        }
      }

      if (shouldShowAlignmentModal()) {
        const isTireAlignmentAllowed = await hasTireAlignmentEnabled(
          formValues?.allocations[0]?.storeNum
        );
        if (isTireAlignmentAllowed) {
          showAlignmentModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      runSubmit({
        ...withLegalEngineRmsItemNumbers(formValues, either.value),
        poStatus: PoStatus.SUBMITTED,
      });
    } else {
      const userFacingLegalError = either.value.errors.find(
        (e: any) => e.code === LegalEngineErrorCode.UNABLE_TO_COMPUTE
      );

      if (userFacingLegalError) {
        setIsSubmitting(false);
      } else if (showLocalSourceRampLayout) {
        setIsSubmitting(false);
        handleFailedPO();
      } else {
        const either: Either<PurchaseOrder, APIError> =
          await handledCreateDraftPurchaseOrder(formValues);
        setIsSubmitting(false);
        if (either.isLeft()) {
          redirectAfterLegalEngineFail();
        }
      }
    }
  };

  const handleFailedPO = (isDraftPO = false, errorMessage?: string) => {
    setAlertMessage(
      isDraftPO ? DRAFT_PO_ERROR_MESSAGE : SUBMIT_PO_ERROR_MESSAGE
    );
    setServerResponseMessage(errorMessage || '');
    setAlertType('error');
    setShowAlertModal(true);
    setIsSubmitting(false);
  };

  const handleAlertModalClose = () => {
    if (alertType === 'success' && alertMessage !== CANCELLED_SUCCESS) {
      setIsSubmittedPOReload(true);
    } else if (
      alertMessage === SUBMIT_ERROR_MESSAGE &&
      !showLocalSourceRampLayout
    ) {
      setIsDraftPOReload(true);
    }
    setShowAlertModal(false);
  };
  const handleCameraDetectModalClose = () => {
    setIsSubmittedPOReload(true);

    setShowCameraDetectModal(false);
  };

  const { run: runSaveDraft, isPending: isPendingSaveDraft } = useAsync({
    deferFn: ([purchaseOrderFormValues]: PurchaseOrderFormValues[]): Promise<
      PurchaseOrder | PurchaseOrder[]
    > =>
      createDraftPurchaseOrder({
        ...purchaseOrderFormValues,
        isLocalSourceRamp: false,
      }),
    onResolve: redirectToDraftPurchaseOrders,
    onReject: (error: any) => {
      handleFailedPO(true, error?.errors[0]?.message);
    },
  });

  const { run: runSubmit, isPending: isPendingSaveSubmit } = useAsync({
    deferFn: async ([
      purchaseOrderFormValues,
    ]: PurchaseOrderFormValues[]): Promise<PurchaseOrder | PurchaseOrder[]> => {
      const prefill = showLocalSourceRampLayout
        ? prefillClassificationsRef.current
        : null;

      const poCreateRequest = {
        ...purchaseOrderFormValues,
        isLocalSourceRamp: showLocalSourceRampLayout,
        rmsItemNumber: resolveRmsItemNumber(
          purchaseOrderFormValues,
          showLocalSourceRampLayout,
          scannerOutput
        ),
        scanMatchIdentifier: scannerOutput?.scanMatchIdentifier
          ? scannerOutput.scanMatchIdentifier.toString()
          : determineLocalSourceScanMatchId(purchaseOrderFormValues, prefill),
        ...(!scannerOutput?.scanMatchIdentifier && {
          scanMatchIdentifierPerItem: determineLocalSourceScanMatchIds(
            purchaseOrderFormValues,
            prefill
          ),
        }),
      };

      const response = await createSubmitPurchaseOrder(
        poCreateRequest,
        globalContext
      );
      // eslint-disable-next-line no-console
      console.log('PO Submission API response:', response);
      // Extract MPO number from response and set for camera modal
      let mpoNumber = '';
      let rmsItemNumber = '';
      let manualPurchaseOrderId = '';
      if (
        Array.isArray(response) &&
        response[0] &&
        (response[0] as any).purchaseOrder?.number
      ) {
        mpoNumber = String((response[0] as any).purchaseOrder.number);
        rmsItemNumber = String(
          (response[0] as any).purchaseOrder.purchaseOrderDetails[0]
            ?.rmsItemNumber
        );
        manualPurchaseOrderId = String((response[0] as any).purchaseOrder.id);
        // eslint-disable-next-line no-console
        console.log('runSubmit: Setting MPO number:', mpoNumber); // Log MPO number
        setCameraModalMpoNumber(mpoNumber); // Set MPO number
        setRmsItemNumber(rmsItemNumber);
        setmanualPurchaseOrderId(manualPurchaseOrderId);

        if (showLocalSourceRampLayout && purchaseOrderFormValues.spendLimitId) {
          await updateFunding(purchaseOrderFormValues.spendLimitId, mpoNumber);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error(
          'runSubmit: Failed to retrieve MPO number from response:',
          response
        );
      }
      return response;
    },
    onResolve: redirectToDraftPurchaseOrders,
    onReject: () => handleFailedPO(),
  });

  const showLoader =
    !configFlagsLoaded ||
    isPendingSaveDraft ||
    isPendingSaveSubmit ||
    isSubmitting;

  const redirectAfterLegalEngineFail = () => {
    setIsSaveDraftDisabled(true);

    setShowAlertModal(true);
    setAlertType('error');
    setAlertMessage(SUBMIT_ERROR_MESSAGE);
  };

  const states = illegalResponse.map(
    (item: LegalEngineResponseItem) =>
      ({
        adjustedTerm: item?.priceInfo?.weeklyTerm,
        totalCostLimit: item?.priceInfo?.totalCost,
        state: getStoreByNumber(stores, item?.storeNumber)?.state,
      } as IllegalStateInfo)
  );

  const uniqueStatesWithIllegalValues: IllegalStateInfo[] = chain(states)
    .uniqBy((item: IllegalStateInfo) => item.state.name)
    .value();

  const onSaveDraft = async () => {
    const firstAllocationStoreNum = 'allocations[0].storeNum';

    await trigger([firstAllocationStoreNum, PoFormField.TotalQuantity]);

    if (get(errors, firstAllocationStoreNum) || hasMaxQuantityError(errors)) {
      clearErrors();
      setError('allocations[0].storeNum', {
        message: 'Please select a store!',
      });
      return;
    }

    const formValues: PurchaseOrderFormValues = getValues();
    if (checkMaxValue(formValues)) {
      setAlertMessage(EXCEED_MAX_MESSAGE);
      setAlertType('error');
      setShowAlertModal(true);
      setIsSubmitting(false);
      return;
    }

    setIsSaveDraftDisabled(true);
    setIsCancelDisabled(true);
    runSaveDraft(formValues);
  };

  const setInitDetailsValues = (input: QrScannerOutput) => {
    setScannerOutput(input);
  };

  const setIsCoupaOrPcrdSelected = (isCoupaOrPcrdSelected: boolean) => {
    setCoupaOrPcrdSelected(isCoupaOrPcrdSelected);
  };

  const fetchPurchaseOrder = async () => {
    try {
      if (!purchaseOrderId) return;

      const request = {
        purchaseOrderNumber: purchaseOrderId,
        storeNumber: [storeNumber],
      };

      const purchaseOrder = await getPurchaseOrderById(request);
      const purchaseOrderValue = {
        ...purchaseOrder?.purchaseOrders[0],
        poStatus: purchaseOrder?.purchaseOrders[0]?.poStatusType,
      };
      purchaseOrder && setPurchaseOrder(purchaseOrderValue);
    } catch (error: any) {
      setIsSaveDraftDisabled(false);
    }
  };

  const handleCancelPOModalResolve = (value: string) => {
    if (value === 'success') {
      setAlertMessage(CANCELLED_SUCCESS);
      setAlertType('success');
      fetchPurchaseOrder();
    } else {
      setAlertMessage(value);
      setAlertType('error');
    }
    setOpenCancelModal(false);
    setShowAlertModal(true);
  };

  const handleCancelPOModalClose = () => {
    setOpenCancelModal(false);
    setShowAlertModal(false);
  };

  if (!showLooseWorkModal && redirectPath) {
    return <Redirect to={redirectPath} />;
  }
  return (
    <>
      {showLooseWorkModal && (
        <LooseWorkModal
          onClose={() => {
            setRedirectPath('');
            setShowLooseWorkModal(false);
          }}
          handleCancelButtonClick={() => {
            setShowLooseWorkModal(false);
          }}
          isEditPo={isEditMode}
        />
      )}
      {showEditSLA && (
        <SecondFactor
          setTwoFactorCancelClick={() => SLACanceled()}
          setTwoFactorCompleted={handleSLACompleted}
          moduleName={'CustomerRetention'}
          CurrentRole={currentRole || DEFAULT_ROLE}
        />
      )}
      <PODetailsBreadcrumb
        purchaseOrderId={purchaseOrderId}
        onClick={isDirty ? (event, href) => setRedirectPath(href) : undefined}
      />
      {showAlertModal && (
        <AlertModal
          onClose={() => handleAlertModalClose()}
          alertType={alertType}
          alertMessage={alertMessage}
          serverResponse={serverResponseMessage}
        />
      )}
      {showCameraDetectModal && (
        <CameraDetectConfirmModal
          onClose={() => handleCameraDetectModalClose()}
          message={cameraDetectMessage}
          onResolve={() => {
            // eslint-disable-next-line no-console
            console.log(
              'CameraDetectConfirmModal resolved, MPO number:',
              cameraModalMpoNumber
            );
            setShowCameraDetectModal(false);
            setShowCameraModal(true);
          }}
        />
      )}
      {showCameraModal &&
        (() => {
          // eslint-disable-next-line no-console
          console.log(
            'Opening CameraCaptureModal with MPO number:',
            cameraModalMpoNumber
          );
          return (
            <CameraCaptureModal
              manualPurchaseOrderId={manualPurchaseOrderId}
              mpoNumber={cameraModalMpoNumber}
              rmsItemNumber={rmsItemNumber}
              isOpen={showCameraModal}
              onClose={() => setShowCameraModal(false)}
              onSave={() => {
                setShowCameraModal(false);
              }}
            />
          );
        })()}
      {openCancelModal && (
        <CancelPOConfirmModal
          id={purchaseOrderId || ''}
          onClose={handleCancelPOModalClose}
          onResolve={(value) => handleCancelPOModalResolve(value)}
        />
      )}
      <RACModal
        isOpen={illegalResponse.length !== 0 && !isTireItem}
        classes={{
          dialogContent: classes.dialogContent,
          dialog: classes.dialogRoot,
          dialogActions: classes.dialogActions,
        }}
        maxWidth="sm"
        title=""
        content={
          <IllegalValueBox
            illegalResponse={illegalResponse}
            stores={stores}
            poNumber={purchaseOrderId}
          />
        }
        onClose={() => {
          noOp();
        }}
        buttons={
          <>
            <RACButton
              variant="contained"
              color="primary"
              onClick={() => {
                autofixRate(uniqueStatesWithIllegalValues);
              }}
            >
              OK
            </RACButton>
          </>
        }
      />

      {showLoader && <LoadingOverlay />}

      <FormProvider {...methods}>
        {isDraftPOReload && (
          <Redirect
            to={{
              pathname: AppRoute.POs,
              search: '?draft',
            }}
          />
        )}
        {isSubmittedPOReload && <Redirect to={AppRoute.POs} />}
        <RACModal
          isOpen={isAlignmentModalOpen}
          classes={{
            dialog: classes.dialogRoot,
            dialogActions: classes.dialogActions,
          }}
          maxWidth="xs"
          title=""
          content={
            <AlignmentBox
              onConfirm={handleSubmit(onSaveSubmitted)}
              purchaseOrder={purchaseOrder}
            />
          }
          onClose={() => {
            noOp();
          }}
          buttons={<></>}
        />
        {configFlagsLoaded && (
          <form
            id="createPOForm"
            onSubmit={methods.handleSubmit(onSaveSubmitted)}
            noValidate
          >
            {purchaseOrderId ? (
              <input
                type="hidden"
                name={PoFormField.Id}
                defaultValue={purchaseOrderId}
                ref={register}
              />
            ) : null}
            <input
              type="hidden"
              name="productCatalogItemsJson"
              ref={register}
            />
            <input
              type="hidden"
              name="dashboardRmsItemNumbers"
              ref={register}
            />
            <input
              type="hidden"
              name={PoFormField.RmsItemNumber}
              ref={register}
            />
            <input type="hidden" name="spendLimitId" ref={register} />
            <input type="hidden" name={PoFormField.PoStatus} ref={register} />
            <DetailsTitle
              setInitDetailsValues={setInitDetailsValues}
              purchaseOrderId={purchaseOrderId}
              purchaseOrder={purchaseOrder}
              disabled={isViewMode}
              isEditMode={isEditMode}
              isCanceled={isCanceled}
              isLocalSourceRampLayout={useLocalSourceView}
              setIsCoupaOrPcrdSelected={setIsCoupaOrPcrdSelected}
            />
            {!useLocalSourceView ? (
              <ItemDetails
                purchaseOrder={purchaseOrder}
                isViewMode={isViewMode}
              />
            ) : null}
            {useLocalSourceView ? (
              <LocalSourceRampDetails
                purchaseOrder={purchaseOrder}
                stores={stores}
                onSearch={onSearch}
                isSearchLoading={isSearchLoading}
                disabled={isViewMode || !showLocalSourceRampLayout}
                qrScannerOutput={scannerOutput}
                isCoupaOrPcrdSelected={coupaOrPcrdSelected}
              />
            ) : (
              <>
                <InventoryDetails
                  purchaseOrder={purchaseOrder}
                  qrScannerOutput={scannerOutput}
                  setIsCoupaOrPcrdSelected={coupaOrPcrdSelected}
                />
                <div className={clsx(classes.flex, classes.bottom)}>
                  <Allocations
                    purchaseOrder={purchaseOrder}
                    stores={stores}
                    onSearch={onSearch}
                    isSearchLoading={isSearchLoading}
                    disabled={isViewMode}
                  />
                  <PricingWrapper
                    purchaseOrder={purchaseOrder}
                    qrScannerOutput={scannerOutput}
                  />
                </div>
              </>
            )}
            <CreatePOFooter
              isEditPo={isEditMode}
              isDirty={canLoseWorkPromptBeShown}
            >
              {(!purchaseOrderId || isEditMode) && (
                <>
                  {!showLocalSourceRampLayout && (
                    <RACButton
                      className={clsx(classes.footerButton)}
                      variant="contained"
                      size="small"
                      color="primary"
                      key="saveAsDraft"
                      onClick={onSaveDraft}
                      disabled={isPendingSaveDraft || isPendingSaveSubmit}
                      loading={isPendingSaveDraft}
                    >
                      Save as Draft
                    </RACButton>
                  )}
                  <RACButton
                    type="submit"
                    form="createPOForm"
                    className={clsx(classes.footerButton)}
                    variant="contained"
                    size="small"
                    color="primary"
                    key="submitPO"
                    loading={isPendingSaveSubmit}
                    disabled={
                      isPendingSaveDraft ||
                      isPendingSaveSubmit ||
                      (hasSpendLimitIdOnPO && !showLocalSourceRampLayout)
                    }
                  >
                    Submit PO
                  </RACButton>
                </>
              )}
              {isViewMode && (
                <>
                  {!isCreateMode && (
                    <RACButton
                      className={clsx(classes.footerButton)}
                      variant="contained"
                      size="small"
                      color="primary"
                      key="saveAsDraft"
                      onClick={handleDuplicate}
                      disabled={hasSpendLimitIdOnPO}
                    >
                      Duplicate PO
                    </RACButton>
                  )}
                  {!isCanceled && (
                    <RACButton
                      className={clsx(classes.footerButton, classes.redButton)}
                      variant="contained"
                      size="small"
                      color="primary"
                      key="submitPO"
                      disabled={isCancelDisabled}
                      onClick={() => {
                        setOpenCancelModal(true);
                      }}
                    >
                      Cancel PO
                    </RACButton>
                  )}
                </>
              )}
            </CreatePOFooter>
          </form>
        )}
      </FormProvider>
    </>
  );
};
